import { shallow } from "zustand/shallow";
import ButtonGroup from "../../../components/Forms/incidentForm/ButtonGroup";
import { useStoreWrapper, useViewStateWrapper } from "../../../store/store";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useIncident from "../../../hooks/useIncident";
import {
  IncidentStatus,
  TabOptions,
} from "../../../types/fieldInstance.interface";
import { useEffect, useRef, useState } from "react";
import Spinner from "react-spinner-material";
import { FormHeader } from "../../../components/Forms/incidentForm/styles";
import { PageTitle } from "../Forms/formStyles.style";
import LastModifiedDate from "../../../components/Forms/incidentForm/LastModifiedDate";
import { IncidentNumber } from "../../../screens/Incidents/approvals/component/approval-list/style";
import {
  IncidentSummaryGrid,
  IncidentSummaryItem,
  IncidentSummaryLabel,
  IncidentSummaryValue,
} from "../../../components/Forms/incidentForm/styles";
import { formatDisplayDateTime } from "../../../components/Forms/dateTimeInput/methods";
import { PLACEHOLDER_VALUE } from "../../../shared/sharedConstants";
import useIncidentTypes from "../../../hooks/useIncidentTypes";
import {
  convertFilesToBase64,
  prependMimeType,
  resetDeleteConfig,
  resetViewConfig,
} from "../../../utils/helpers";
import { toastService } from "../../../utils/helpers/toastService";
import CustomLoader from "../../../components/CustomLoader";
import AlertDialog from "../../User/Users/components/AlertDialog/AlertDialog";
import { getFacilityEditAttachmentsPermissions } from "../../../utils/formGenUtils/getFacilityPermissions";
import { useIncidentData } from "../../../layouts/incidentLayout";
import useModeLock from "../../../hooks/useModeLock";
import ErrorMessage from "../../../components/Forms/incidentForm/ErrorMessage";
import { defaultState } from "../../../components/Forms/incidentForm/methodsAndHandlers";
import {
  AttachmentLineItem,
  FileViewModal,
  SelectedFilesView,
} from "./AttachmentExports";
import { useMultipleAttachments } from "../../../hooks/useAttachments";

const Attachments = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { incidentTypes } = useIncidentTypes();
  const [selectedFiles, setSelectedFiles] = useState<any>([]);
  const [isFileSizeTooLarge, setIsFileSizeTooLarge] = useState<boolean>(false);
  const [shouldRenderNull, setShouldRenderNull] = useState(false);
  const [shouldShowLoader, setShouldShowLoader] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [attachmentId, setAttachmentId] = useState("");
  const [fileViewConfig, setFileViewConfig] = useState(resetViewConfig());
  const [deleteConfig, setDeleteConfig] = useState(resetDeleteConfig());
  const [showUpload, setShowUpload] = useState(false);

  const MAX_TOTAL_SIZE_MB = 4;

  function setFileSizeTooLarge(files: any[]) {
    const totalSize = files.reduce((acc, file) => acc + file.data.size, 0);

    console.log(totalSize, MAX_TOTAL_SIZE_MB * 1024 * 1024);

    const isTooLarge = totalSize > MAX_TOTAL_SIZE_MB * 1024 * 1024;

    setTotalSize(totalSize);

    setIsFileSizeTooLarge(isTooLarge);

    return isTooLarge;
  }

  const handleFileDelete = async (ind: number) => {
    let _files = [...selectedFiles];

    _files.splice(ind, 1);

    setSelectedFiles(_files);

    setDeleteConfig(resetDeleteConfig());

    const base64Files = await convertFilesToBase64(_files as [], userId);

    const isTooLarge = setFileSizeTooLarge(_files);

    !isTooLarge && setAttachments(base64Files);
  };

  const handleFileChange = async (e: any) => {
    let files = Array.from(e.target.files);

    let _files = [...selectedFiles];

    files = files.map((file: any) => ({
      name: file.name,
      data: file,
    }));

    _files.push(...files);

    const isTooLarge = setFileSizeTooLarge(_files);

    setSelectedFiles(_files);

    const base64Files = await convertFilesToBase64(_files as [], userId);

    !isTooLarge && setAttachments(base64Files);

    setShowUpload(true);
  };

  const { forms, userId, attachments, setAttachments, userFacilities } =
    useStoreWrapper(
      (state) => ({
        forms: state.forms,
        userId: state.user.userId,
        userName: `${state.user.firstName} ${state.user.lastName}`,
        userFacilities: state.userFacilities,
        resetFormState: state.resetForm,
        attachments: state.attachments,
        setAttachments: state.setAttachments,
      }),
      shallow
    );

  const { incidentIdOrNew: incidentID } = useParams();

  const { setIncidentFormState } = useIncidentData();

  const navigate = useNavigate();
  const formId = incidentID || "";
  const {
    baseFormData: { incidentType, facilityId },
    formState: { incidentStatus },
  } = forms[formId] || defaultState;

  const formPageTitle = `${getIncidentLabel(incidentType)} - ${
    TabOptions.Attachments
  }`;

  const {
    postIncidentAttachment: {
      isLoading: postAttachmentLoading,
      mutateAsync: postAttachmentMutateAsync,
    },
    getIncident: {
      data: incidentData,
      isSuccess: incidentDataSuccess,
      isLoading: incidentDataLoading,
    },
    handleGetIncidentAttachments,
    deleteIncidentAttachment: {
      isLoading: isDeleting,
      mutateAsync: deleteAttachmentMutateAsync,
      isSuccess: deleteAttachmentSuccess,
    },
    handleGetSingleAttachments,
  } = useIncident({
    incidentId: formId,
    attachmentId,
  });

  useEffect(() => {
    if (deleteAttachmentSuccess) {
      setAttachmentId("");
      refetchAttachments();
    }
  }, [deleteAttachmentSuccess]);

  const {
    data: _incidentAttachments,
    refetch: refetchAttachments,
    isLoading,
  } = handleGetIncidentAttachments;

  const { data: _incidentAttachment, isLoading: isLoadingAttachment } =
    handleGetSingleAttachments;

  useEffect(() => {
    if (_incidentAttachment !== undefined) {
      setFileViewConfig({
        ...fileViewConfig,
        viewFile: true,
        file: prependMimeType(
          _incidentAttachment?.data?.data,
          fileViewConfig.fileExtension
        ),
      });
    }
  }, [_incidentAttachment]);

  const incidentAttachments = _incidentAttachments?.data?.data || [];

  // Fetch base64 data for all attachments so downloads work
  const { data: attachmentsWithData } =
    useMultipleAttachments(incidentAttachments);

  function getIncidentObj() {
    return incidentDataLoading || !incidentDataSuccess
      ? null
      : incidentData?.data?.data;
  }

  const selectedIncident = getIncidentObj();

  const incidentReturnTo = useViewStateWrapper((state) => state.incidentReturnTo);
  const [searchParams, setSearchParams] = useSearchParams();
  const editModeActive = searchParams.get("mode") === "edit";

  function setEditModeActive(value: boolean) {
    setSearchParams({ mode: value ? "edit" : "view" });
  }

  function _handleDiscard(path?: string) {
    (() => {
      return new Promise<void>((resolve, reject) => {
        setIncidentFormState({ shouldResetForm: true });
        resolve();
      });
    })().then(() => {
      navigate(path ?? incidentReturnTo ?? "/user/incidents-list");
    });
  }

  function shouldShowSaveButton() {
    if (selectedIncident) {
      return incidentStatus === IncidentStatus.Draft;
    }
    return true;
  }

  function getSubmitButtonName() {
    if (selectedIncident && incidentStatus !== IncidentStatus.Draft) {
      return postAttachmentLoading ? (
        <Spinner radius={24} color={"white"} stroke={2} visible={true} />
      ) : (
        "Resubmit"
      );
    } else {
      return postAttachmentLoading ? (
        <Spinner radius={24} color={"white"} stroke={2} visible={true} />
      ) : (
        "Submit"
      );
    }
  }

  function isCreateButtonDisabled() {
    if (!attachments.length || isFileSizeTooLarge) {
      return true;
    }
    return postAttachmentLoading;
  }

  function getIncidentLabel(incidentType: string) {
    const selectedIncidentType = incidentTypes.find(
      (incident) => incident.id === incidentType
    );
    return selectedIncidentType ? selectedIncidentType.label : "";
  }

  const handleDeleteMethod = (attachmentId: string) => {
    setAttachmentId(attachmentId);
    deleteAttachmentMutateAsync(attachmentId, {
      onSuccess: (res) => {
        toastService.success(
          res.data.message || "Attachment deleted Successfully!!!"
        );
        setDeleteConfig(resetDeleteConfig());
        setFileViewConfig(resetViewConfig());
      },
    });
  };

  const { canUserEditPostSubmission } = getFacilityEditAttachmentsPermissions(
    userFacilities,
    facilityId,
    incidentStatus,
    userId,
    selectedIncident?.Incident_Created_By__c
  );

  useModeLock({
    canUserEditPostSubmission,
    editModeActive,
    setSearchParams,
    incidentStatus,
  });

  function getErrorMessage(errorMessage: string) {
    // this function can be reworked.
    let displayedError = "";
    displayedError = errorMessage.includes("Unrecognized base64 character")
      ? "You may have an invalid file type selected. Only JPG, PNG, DOCX, PDF, and CSVs allowed."
      : "Something went wrong. Please review and try again.";
    return displayedError;
  }

  function handleAttachments() {
    postAttachmentMutateAsync(attachments, {
      onSuccess: (res: any) => {
        toastService.success(
          `${res.data?.message}` || "Attachments Created Successfully!!!"
        );
        refetchAttachments();
        setSelectedFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setAttachments([]);
        setEditModeActive(false);
      },
      onError: (err: any) => {
        const userFriendlyErrorMsg = err?.response?.data?.userFriendlyErrorMessage;
        toastService.error(
          userFriendlyErrorMsg ||
            getErrorMessage(err?.response?.data?.message ?? err.message)
        );
        console.log(err);
      },
    });
  }

  useEffect(() => {
    if (selectedFiles.length < 1) setShowUpload(false);
  }, [selectedFiles]);

  useEffect(() => {
    setShouldShowLoader(
      postAttachmentLoading || isLoading || incidentDataLoading
    );
  }, [postAttachmentLoading, isLoading, incidentDataLoading]);

  useEffect(() => {
    const _shouldRenderNull = !incidentDataSuccess || !forms[formId];
    console.log(incidentDataSuccess);
    setShouldRenderNull(_shouldRenderNull);
  }, [!incidentDataSuccess, forms[formId]]);

  function onExit() {
    navigate("/");
  }

  if (shouldRenderNull) {
    return <ErrorMessage onExit={onExit} />;
  } else {
    return shouldShowLoader ? (
      <div>
        <CustomLoader fullscreen />
      </div>
    ) : (
      <>
        <div>
          <ButtonGroup
            attachFiles={handleAttachments}
            isCreateButtonDisabled={isCreateButtonDisabled()}
            shouldShowSaveButton={shouldShowSaveButton()}
            getSubmitButtonName={getSubmitButtonName}
            isAttachments
            canUserAddAttachments={canUserEditPostSubmission}
            setAttachmentsMode={setSearchParams}
            attachmentsMode={editModeActive}
            handleDiscardFromAttachments={_handleDiscard}
          />
          <FormHeader>
            <PageTitle>{formPageTitle}</PageTitle>
            <div>
              {selectedIncident?.Incident_Name__c && (
                <IncidentNumber style={{ textAlign: "right" }}>
                  Incident Number: {selectedIncident?.Incident_Name__c}
                </IncidentNumber>
              )}
              <LastModifiedDate
                lastModifiedDate={selectedIncident?.LastModifiedDate}
              />
            </div>
          </FormHeader>
          {/* Incident summary: Resident Name / Date-Time / Status */}
          {(() => {
            const residentName =
              selectedIncident?.Resident_Name__c || PLACEHOLDER_VALUE;
            const rawIncidentDateTime =
              selectedIncident?.Incident_Date_Time__c || null;
            const formattedIncidentDateTime = formatDisplayDateTime(
              rawIncidentDateTime
            );
            const statusDisplay =
              selectedIncident?.Incident_Status__c || PLACEHOLDER_VALUE;

            const incidentSummaryItems = [
              { label: "Resident Name", value: residentName },
              { label: "Date/Time of Incident", value: formattedIncidentDateTime },
              { label: "Status", value: statusDisplay },
            ];

            const shouldShowIncidentSummary = incidentSummaryItems.some(
              (item) => item.value && item.value !== PLACEHOLDER_VALUE
            );

            return shouldShowIncidentSummary ? (
              <IncidentSummaryGrid data-testid="incident-summary-grid">
                {incidentSummaryItems.map(({ label, value }) => (
                  <IncidentSummaryItem key={label}>
                    <IncidentSummaryLabel>{label}</IncidentSummaryLabel>
                    <IncidentSummaryValue>{value}</IncidentSummaryValue>
                  </IncidentSummaryItem>
                ))}
              </IncidentSummaryGrid>
            ) : null;
          })()}
          <div className="bg-white p-6">
            <div className="border-2 border-dashed border-gray-200 p-4 text-center">
              <div className="mb-8">
                <span
                  className={editModeActive ? "text-gray-500" : "text-gray-200"}
                >
                  Drop a file here or
                  <label
                    htmlFor="files"
                    className={`${
                      editModeActive ? "text-blue-500" : "text-blue-200"
                    } cursor-pointer`}
                  >
                    {""} choose a file
                  </label>
                </span>
                {editModeActive && (
                  <span className="block pt-3 text-xs text-red-400">
                    Acceptable files .pdf .jpg .docx .png
                  </span>
                )}
                <input
                  style={{ display: "none" }}
                  type="file"
                  id="files"
                  multiple
                  ref={fileInputRef}
                  disabled={!editModeActive}
                  accept="application/pdf, image/jpeg, image/png, image/gif, image/bmp, image/tiff, .docx, .csv"
                  onChange={handleFileChange}
                  className="ml-2 text-blue-600 hover:text-blue-500 focus:underline focus:outline-none"
                />
              </div>
              {!editModeActive && canUserEditPostSubmission && (
                <div className="text-red-400">
                  Click{" "}
                  <button
                    onClick={() => setEditModeActive(true)}
                    className="cursor-pointer border-0 bg-transparent text-blue-600"
                  >
                    HERE
                  </button>{" "}
                  or the edit button to add attachments
                </div>
              )}
            </div>
            <div className="mt-4">
              <ul className="p-0">
                {selectedFiles.length > 0 && (
                  <SelectedFilesView
                    selectedFiles={selectedFiles}
                    setDeleteConfig={setDeleteConfig}
                    handleFileDelete={handleFileDelete}
                    isTooLarge={isFileSizeTooLarge}
                    fileSize={totalSize}
                  />
                )}
                {attachmentsWithData.map((attachment: any) => {
                  const enrichedAttachment = {
                    ...attachment,
                    data: attachment.base64Data,
                  };
                  return (
                    <AttachmentLineItem
                      key={attachment?.attachmentId}
                      attachment={enrichedAttachment}
                      setAttachmentId={setAttachmentId}
                      setFileViewConfig={setFileViewConfig}
                      fileViewConfig={fileViewConfig}
                      setDeleteConfig={setDeleteConfig}
                      handleDeleteMethod={handleDeleteMethod}
                      showDelete
                    />
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
        {fileViewConfig.viewFile && (
          <FileViewModal
            {...{
              fileViewConfig,
              setFileViewConfig,
              isLoadingAttachment,
              setAttachmentId,
            }}
          />
        )}
        {deleteConfig.showDelete && (
          <AlertDialog
            message="Delete Uploaded File ?"
            actionButtonText="Delete"
            btnPos="center"
            onConfirm={() => {
              deleteConfig.fn();
            }}
            onCancel={() => setDeleteConfig(resetDeleteConfig())}
            loading={isDeleting}
          />
        )}
        {showUpload && (
          <AlertDialog
            message={
              <SelectedFilesView
                selectedFiles={selectedFiles}
                setDeleteConfig={setDeleteConfig}
                handleFileDelete={handleFileDelete}
                single
                isTooLarge={isFileSizeTooLarge}
                fileSize={totalSize}
              />
            }
            actionButtonText="Upload"
            btnPos="center"
            onConfirm={() => {
              setShowUpload(false);
              handleAttachments();
            }}
            onCancel={() => setShowUpload(false)}
            disableActionButton={isFileSizeTooLarge}
          />
        )}
      </>
    );
  }
};

export default Attachments;
